import type { Payment } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import {
  type ApolloError,
  type DocumentNode,
  gql,
  useMutation,
} from "@apollo/client";
import { Decimal } from "decimal.js";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export type CreatePaymentInput = {
  amount: number | null;
  executionDate: Date | null;
  recipientName: string | null;
  recipientIban: string | null;
  senderIban: string | null;
  purpose: string | null;
  timestamp: Date | null;
  currency: string | null;
};

type CreatePaymentResponse = {
  createPayment: Payment;
};

export const useCreatePayment = ({
  refetchQueries,
}: {
  refetchQueries?: string[] | DocumentNode[];
} = {}): [
  (
    draftPaymentIds: string[],
    input: Partial<CreatePaymentInput>,
  ) => Promise<Payment>,
  {
    data: Payment | undefined;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [createPayment, { loading, error, data }] =
    useMutation<CreatePaymentResponse>(CREATE_PAYMENT_MUTATION, {
      refetchQueries,
    });

  const handleCreatePayment = async (
    draftPaymentIds: string[],
    input: Partial<CreatePaymentInput>,
  ): Promise<Payment> => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    const toastId = toast.loading(t("generic_loading"));

    try {
      const { data: response } = await createPayment({
        variables: {
          tenantId,
          draftPaymentIds,
          ...input,
          ...(input.amount != null && {
            amount: new Decimal(input.amount).mul(100).toNumber(),
          }),
          executionDate: input.executionDate,
          timestamp: input.timestamp ?? new Date(),
        },
      });

      toast.dismiss(toastId);

      if (!response?.createPayment) {
        throw new Error("Failed to create payment");
      }

      return response.createPayment;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return [handleCreatePayment, { data: data?.createPayment, loading, error }];
};

export const CREATE_PAYMENT_MUTATION = gql`
  mutation CreatePayment(
    $tenantId: String!
    $draftPaymentIds: [String!]!
    $amount: Float
    $currency: String
    $executionDate: String
    $recipientName: String
    $recipientIban: String
    $senderIban: String
    $purpose: String
    $timestamp: String!
  ) {
    createPayment(
      tenantId: $tenantId
      draftPaymentIds: $draftPaymentIds
      amount: $amount
      currency: $currency
      executionDate: $executionDate
      recipientName: $recipientName
      recipientIban: $recipientIban
      senderIban: $senderIban
      purpose: $purpose
      timestamp: $timestamp
    ) {
      bankTxnProjectionId
      bankTxnId
      tenantId
      status
      amount
      createdAt
      recipientName
      recipientIban
      sequenceNumber
      currency
      senderIban
      purpose
      executionDate
    }
  }
`;
